import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Download, Loader2, 
  DollarSign, Users, AlertCircle, FileSpreadsheet, 
  CheckCircle2, Clock, UserMinus, Building2, Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { reportsService } from '../../services/reportsService';
import { useAuth } from '../../context/AuthContext';

const Reports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('financial');
  const [data, setData] = useState(null);
  const [deptStats, setDeptStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const isFinanceOrCEO = user && (user.role === 'super_admin' || user.role === 'finance_manager');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'financial') {
        const res = await reportsService.getExecutiveDashboard();
        setData(res.data);
      } else {
        const dashRes = await reportsService.getAttendanceDashboard();
        const deptRes = await reportsService.getDepartmentStats();
        setData(dashRes.data);
        setDeptStats(deptRes.data || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Agar user finance role ka nahi hai aur wo financial tab pe hai, to use attendance pe bhej do
    if (activeTab === 'financial' && !isFinanceOrCEO) {
      setActiveTab('attendance');
      return;
    }
    loadData();
  }, [activeTab]);

  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div className="space-y-6 pb-10 text-gray-900">
      <div>
        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Reports & Analytics</h1>
        <p className="text-sm text-gray-500">Business performance and operational insights</p>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-1 border-b border-gray-100">
        {isFinanceOrCEO && (
          <button 
            onClick={() => setActiveTab('financial')}
            className={'px-6 py-3 text-sm font-semibold border-b-2 transition-all ' + (activeTab === 'financial' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600')}
          >
            Financial Overview
          </button>
        )}
        <button 
          onClick={() => setActiveTab('attendance')}
          className={'px-6 py-3 text-sm font-semibold border-b-2 transition-all ' + (activeTab === 'attendance' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600')}
        >
          Attendance Insights
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-100 text-red-700 rounded-xl text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* FINANCIAL TAB CONTENT */}
          {activeTab === 'financial' && data && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiStat 
                  label="Revenue Received" 
                  value={data?.revenue?.total_received || 0} 
                  icon={<DollarSign className="w-5 h-5" />} 
                  color="text-green-600" 
                />
                <KpiStat 
                  label="Pending Receivables" 
                  value={data?.revenue?.total_pending || 0} 
                  icon={<TrendingUp className="w-5 h-5" />} 
                  color="text-primary" 
                />
                <KpiStat 
                  label="Active Employees" 
                  value={data?.headcount || 0} 
                  icon={<Users className="w-5 h-5" />} 
                  color="text-blue-600" 
                />
                <KpiStat 
                  label="Overdue Milestones" 
                  value={data?.revenue?.total_overdue || 0} 
                  icon={<AlertCircle className="w-5 h-5" />} 
                  color="text-red-600" 
                />
              </div>

              <div className="bg-white rounded-card border border-gray-100 shadow-soft p-6">
                <h2 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 tracking-tight">
                  <Download className="w-4 h-4 text-primary" /> Export Financial Statements
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <ExportButton title="Profit & Loss" desc="Monthly income/expense" link="/reports/profit-loss" onNavigate={handleNavigate} />
                  <ExportButton title="Cash Flow" desc="Inflow/Outflow summary" link="/reports/cash-flow" onNavigate={handleNavigate} />
                  <ExportButton title="Receivables" desc="Outstanding client dues" link="/reports/receivables" onNavigate={handleNavigate} />
                </div>
              </div>
            </>
          )}

          {/* ATTENDANCE TAB CONTENT */}
          {activeTab === 'attendance' && data && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiStat label="Present Today" value={data?.present_today || 0} icon={<CheckCircle2 className="w-5 h-5" />} color="text-green-600" />
                <KpiStat label="Late Arrivals" value={data?.late_today || 0} icon={<Clock className="w-5 h-5" />} color="text-amber-500" />
                <KpiStat label="Absent" value={data?.absent_today || 0} icon={<UserMinus className="w-5 h-5" />} color="text-red-500" />
                <KpiStat label="On Leave" value={data?.on_leave_today || 0} icon={<Building2 className="w-5 h-5" />} color="text-purple-600" />
              </div>

              <div className="bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-gray-900 tracking-tight text-primary uppercase tracking-widest">Department-wise Attendance</h2>
                  <BarChart3 className="w-4 h-4 text-gray-300" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-tighter">
                      <tr>
                        <th className="px-6 py-3">Department</th>
                        <th className="px-6 py-3">Total Staff</th>
                        <th className="px-6 py-3">Present</th>
                        <th className="px-6 py-3 text-right">Attendance Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {deptStats.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-10 text-center text-gray-400">No department stats available today.</td>
                        </tr>
                      ) : (
                        deptStats.map((dept, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-6 py-4 font-semibold text-gray-900">{dept.department}</td>
                            <td className="px-6 py-4 text-gray-600">{dept.total_employees}</td>
                            <td className="px-6 py-4 text-gray-600">{dept.present_today}</td>
                            <td className="px-6 py-4 text-right">
                              <span className={'px-2.5 py-1 rounded-lg font-bold text-xs ' + (dept.attendance_rate > 80 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700')}>
                                {dept.attendance_rate}%
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Internal Stat Card Component
const KpiStat = ({ label, value, icon, color }) => (
  <div className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center gap-4 hover:border-primary/20 transition-colors group">
    <div className={`p-3 rounded-xl bg-gray-50 ${color} group-hover:scale-110 transition-transform`}>{icon}</div>
    <div>
      <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{label}</p>
      <p className="text-xl font-black text-gray-900 mt-0.5">
        {typeof value === 'number' ? value.toLocaleString() : (value || '0')}
      </p>
    </div>
  </div>
);

// Internal Export Button Component
const ExportButton = ({ title, desc, onClick, link, onNavigate }) => (
  <button 
    onClick={() => {
      if (onClick) onClick();
      if (link && onNavigate) onNavigate(link);
    }}
    className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-primary hover:bg-primary/5 transition-all text-left group"
  >
    <div className="p-2.5 bg-gray-50 rounded-xl text-gray-400 group-hover:bg-primary group-hover:text-white transition-all">
      <FileSpreadsheet className="w-5 h-5" />
    </div>
    <div>
      <p className="text-sm font-bold text-gray-900">{title}</p>
      <p className="text-[10px] text-gray-400 uppercase font-medium mt-0.5 tracking-tighter">{desc}</p>
    </div>
  </button>
);

export default Reports;